import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as headerConstants from '../../shared/constants/headerConst';
import { NgFor, NgIf } from '@angular/common';
import { NavigationEnd, Router, RouterEvent, RouterLink } from '@angular/router';
import { TabService } from '../../services/tabs/tab.service';
import { filter, map, Subject, takeUntil } from 'rxjs';
import { IconsComponent } from '../../shared/icons/icons.component';
import { SidebarService } from '../../services/sidebar-btn/sidebar.service';
import { UserService } from '../../services/user/user.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatExpansionModule,
    MatTooltipModule,
    NgFor,
    NgIf,
    RouterLink,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent implements OnInit {
  private userService = inject(UserService);
  private userRole = this.userService.user?.role ?? null;

  isOpen = true;
  isSettingsOpen = false;
  selectedTabIndex = 0;
  private destroy$ = new Subject<void>();

  // Role-based tabs (same as your header)
  SUPERADMIN_TABS: string[] = headerConstants.SUPERADMIN_TABS;
  ADMIN_TABS: string[] = headerConstants.ADMIN_TABS;
  USER_TABS: string[] = headerConstants.USER_TABS;

  visibleTabs: string[] = [];
  tabRoutes: string[] = [];
  activeRoute: string = '';

  // Main navigation items - these appear outside of settings
  mainMenuItems = [
    {
      icon: 'home',
      label: $localize`Home`,
      route: '/home',
      tooltip: 'Home',
      isCustomIcon: false,
    },
    {
      icon: 'instrument_run_icon',
      label: $localize`Instrument Run`,
      route: '/instrument-run',
      tooltip: 'Instrument Run',
      isCustomIcon: true,
    },
    {
      icon: 'sample_icon',
      label: $localize`Sample`,
      route: '/sample-test',
      tooltip: 'Sample Test',
      isCustomIcon: true,
    },
    {
      icon: 'analysis_icon',
      label: $localize`Analysis`,
      route: '/analysis',
      tooltip: 'SV Test Panel / Analysis',
      isCustomIcon: true,
    },
  ];

  iconsBasePath = 'assets/icons';
  // Settings submenu items - these appear inside settings dropdown
  settingsItems: any[] = [];
  customIcon = [
    {
      path: `${this.iconsBasePath}/instrument_run_icon.svg`,
      icon: 'instrument_run_icon',
    },
    {
      path: `${this.iconsBasePath}/sample_icon.svg`,
      icon: 'sample_icon',
    },
    {
      path: `${this.iconsBasePath}/analysis_icon.svg`,
      icon: 'analysis_icon',
    },
    {
      path: `${this.iconsBasePath}/application_config_icon.svg`,
      icon: 'application_config_icon',
    },
  ];

  constructor(
    private router: Router,
    private tabService: TabService,
    public sidebarService: SidebarService,
    private domSanitizer: DomSanitizer,
    public matIconRegistry: MatIconRegistry
  ) {
    for (let index = 0; index < this.customIcon.length; index++) {
      this.matIconRegistry.addSvgIcon(
        this.customIcon[index].icon,
        this.domSanitizer.bypassSecurityTrustResourceUrl(this.customIcon[index].path)
      );
    }
  }

  ngOnInit(): void {
    this.sidebarService.isOpen$.pipe(takeUntil(this.destroy$)).subscribe((isOpen) => {
      this.isOpen = isOpen;
      if (!isOpen) {
        this.isSettingsOpen = false;
      }
    });

    this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.userRole = user?.role ?? null;
      this.setVisibleTabs(user?.role ?? null);
      this.setSettingsItems();
    });

    // Update tab index based on current route
    const currentRoute = this.router.url;
    const savedTabIndex = this.tabRoutes.indexOf(currentRoute);
    this.selectedTabIndex = savedTabIndex !== -1 ? savedTabIndex : 0;
    this.tabService.setActiveTab(this.selectedTabIndex);
    this.handleNavigation();
  }

  setVisibleTabs(userRole: string | null): void {
    if (userRole) {
      const userMgmtRoute = userRole === 'Manager' ? '/user-management/group-management' : '/user-management';
      switch (userRole) {
        case 'Superadmin':
          this.visibleTabs = this.SUPERADMIN_TABS;
          this.tabRoutes = [];
          break;
        case 'Admin':
          this.visibleTabs = this.ADMIN_TABS;
          this.tabRoutes = [
            '/instrument-run',
            '/sv-test-panel',
            '/sample-test',
            userMgmtRoute,
            '/file-management',
            '/analysis',
          ];
          break;
        case 'Manager':
          this.visibleTabs = this.ADMIN_TABS;
          this.tabRoutes = [
            '/instrument-run',
            '/sv-test-panel',
            '/sample-test',
            userMgmtRoute,
            '/file-management',
            '/analysis',
          ];
          break;
        case 'User':
          this.visibleTabs = this.USER_TABS;
          this.tabRoutes = ['/instrument-run', '/sv-test-panel', '/sample-test', '/file-management', '/analysis'];
          break;
        default:
          this.visibleTabs = [];
          this.tabRoutes = [];
      }
    }
  }

  setSettingsItems(): void {
    // Base settings items available to all roles
    this.settingsItems = [
      {
        icon: 'application_config_icon',
        label: $localize`Application Config`,
        route: '/sv-test-panel',
        tooltip: 'Application Configuration',
        isCustomIcon: true,
      },
      {
        icon: 'folder_open',
        label: $localize`File Management`,
        route: '/file-management',
        tooltip: 'File Management',
        isCustomIcon: false,
      },
    ];

    // Add User Management only for Admin, Manager, and Superadmin
    if (this.userRole === 'Admin' || this.userRole === 'Manager' || this.userRole === 'Superadmin') {
      const route = this.userRole === 'Manager' ? '/user-management/group-management' : '/user-management';
      this.settingsItems.push({
        icon: 'people',
        label: $localize`User Management`,
        route: route,
        tooltip: 'User Management',
        isCustomIcon: false,
      });
    }
  }

  toggleSidebar(): void {
    this.isOpen = !this.isOpen;
  }

  toggleSettings(): void {
    if (this.isOpen) {
      this.isSettingsOpen = !this.isSettingsOpen;
    }
  }

  onTabClick(index: number): void {
    const route = this.tabRoutes[index];
    this.selectedTabIndex = index;
    this.tabService.setActiveTab(index);

    if (route) {
      this.router.navigateByUrl(route);
    }
  }

  handleNavigation(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const currentRoute = this.router.url;
        this.activeRoute = this.router.url;
        const tabIndex = this.tabRoutes.indexOf(currentRoute);

        if (event instanceof NavigationEnd) {
          this.activeRoute = event.urlAfterRedirects;
        }

        if (tabIndex !== -1) {
          this.selectedTabIndex = tabIndex;
          this.tabService.setActiveTab(tabIndex);
        }
      });
  }

  getTabIcon(tabName: string): string {
    const iconMap: { [key: string]: string } = {
      Home: 'home',
      'Instrument Run': 'play_circle_outline',
      'SV Test Panel': 'analytics',
      'Sample Test': 'biotech',
      'User Management': 'people',
      'File Management': 'folder_open',
      'Application Config': 'settings_applications',
    };
    return iconMap[tabName] || 'circle';
  }

  // Check if current route is active
  isRouteActive(route: string): boolean {
    return this.router.url === route;
  }

  // Check if any settings route is active
  isSettingsRouteActive(): boolean {
    return this.settingsItems.some((item) => this.router.url === item.route);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
